import { ActionType, UpdateLastVisitedNotificationsAction } from '@/actions/actiontypes';
import { Config } from '@/bootstrap/config';
import AppModal from '@/components/common/AppModal';
import { stateConfig, stateNotifications, stateSplash } from '@/selectors/selectors';
import { Notification } from '@/state';
import { SplashState } from '@/state/SplashState';
import { useDispatch, useSelector } from '@/state/store';
import { useEffect, useMemo, useState } from 'react';

interface AlertEntryProps {
    date: Date;
    message: HTMLElement | string;
    onClick?: () => void;
    link?: string;
}

export interface Feilmelding {
    id: string;
    arsak: 'HENDELSE' | 'MERKNAD' | 'DRIFTSSTANS' | 'VEDLIKEHOLD' | 'INGEN_PROBLEMER';
    pavirkedeSystemer: { systemID: string }[];
    datoLaget: string;
    tittel: string;
    innhold: string;
}

const AlertEntry = ({ date, message, onClick, link }: AlertEntryProps) => {
    const simpleDate = date.toLocaleDateString('nb-no', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
    return (
        <li className="c-notification__alert__entry" onClick={onClick}>
            <p dangerouslySetInnerHTML={{ __html: message }}></p>
            <p className="c-notification__alert__entry__date" title={date.toISOString()}>
                {simpleDate}
            </p>
            {link && (
                <a
                    className="c-notification__alert__link"
                    href={link}
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    Se hendelse (NVDB Status)
                </a>
            )}
        </li>
    );
};

export const fetchFeilmeldinger = async (config: Config) => {
    const response: Feilmelding[] = await fetch(
        `${config.status.api}/api/v1/feilmeldinger/aktiv`
    )
        .then(res => res.json())
        .then(json => json.feilmeldinger)
        .catch(e => {
            console.error('Klarte ikke å hente feilmeldinger', e);
            return { feilmeldinger: [] };
        });
    return response.filter(f =>
        f.pavirkedeSystemer.flatMap(s => s.systemID).includes(config.status.vegkart)
    );
};

const getNewestAlertTime = (
    feilmeldinger: Feilmelding[],
    notifications: Notification[]
): Date => {
    const lastNotification = new Date(notifications[0]?.time).getTime()
        ? new Date(notifications[0]?.time)
        : new Date(0);
    const lastStatusAlert = new Date(feilmeldinger[0]?.datoLaget).getTime()
        ? new Date(feilmeldinger[0]?.datoLaget)
        : new Date(0);
    return new Date(lastNotification).getTime() - new Date(lastStatusAlert).getTime()
        ? lastStatusAlert
        : lastNotification;
};

interface NotificationDialogProps {
    handleWindowChange: (window: SplashState) => void;
}

export const NotificationDialog = ({ handleWindowChange }: NotificationDialogProps) => {
    const modalState = useSelector(stateSplash);
    const config = useSelector(stateConfig);
    const dispatch = useDispatch();

    const [hasIssue, setHasIssue] = useState(false);
    const [statusAlerts, setStatusAlerts] = useState<Feilmelding[]>([]);

    const notifications = useSelector(stateNotifications);
    const notificationsState: Notification[] = useMemo(
        () => notifications.toSorted((a, b) => b.time.getTime() - a.time.getTime()),
        [notifications]
    );

    const handleClose = () => {
        handleWindowChange(SplashState.NONE);
    };

    useEffect(() => {
        fetchFeilmeldinger(config).then(res => {
            if (res.some(f => f.arsak !== 'INGEN_PROBLEMER')) {
                setHasIssue(true);
                setStatusAlerts(
                    res
                        .filter(f => f.arsak !== 'INGEN_PROBLEMER')
                        .sort(
                            (a, b) =>
                                new Date(b.datoLaget).getTime() -
                                new Date(a.datoLaget).getTime()
                        )
                );
            } else {
                setHasIssue(false);
            }
            const action: UpdateLastVisitedNotificationsAction = {
                type: ActionType.LAST_VISITED_NOTIFICATIONS_UPDATED,
                date: getNewestAlertTime(
                    res.sort(
                        (a, b) =>
                            new Date(b.datoLaget).getTime() -
                            new Date(a.datoLaget).getTime()
                    ),
                    notificationsState
                ),
            };
            dispatch(action);
        });
    }, [config, dispatch, notificationsState]);

    const alertType = hasIssue ? 'warning' : 'success';
    const alertMessage = hasIssue
        ? 'Vegkart har en hendelse.'
        : 'Vegkart fungerer som normalt.';
    return (
        <AppModal
            className="c-notification"
            show={modalState === SplashState.NOTIFICATION}
            onClose={handleClose}
            title="Varsler"
        >
            <div className="c-notification__content">
                <div
                    className={`c-notification__status c-notification__status--${alertType}`}
                >
                    <i
                        className="material-icons c-notification__status-icon"
                        aria-hidden="true"
                    >
                        {hasIssue ? 'warning' : 'check_circle'}
                    </i>
                    <div className="c-notification__alert__content">
                        <p>{alertMessage}</p>
                        <p>
                            <a
                                href={config.status.url}
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                Se hendelser (NVDB Status)
                            </a>
                        </p>
                    </div>
                </div>
                <ul className="c-notification__alert">
                    {notificationsState.map((n, i) => (
                        <AlertEntry
                            key={'alert' + i.toString()}
                            date={n.time}
                            message={n.message}
                            onClick={n.callback}
                        />
                    ))}
                    {statusAlerts.length > 0 &&
                        statusAlerts.map(issue => (
                            <AlertEntry
                                key={`status-${issue.id}`}
                                date={new Date(issue.datoLaget)}
                                message={issue.tittel}
                                link={config.status.url + '/#/hendelser?id=' + issue.id}
                            />
                        ))}
                    <li className="c-notification__alert__end">
                        {notificationsState.length || statusAlerts.length
                            ? 'Ingen eldre varsler'
                            : 'Ingen varsler'}
                    </li>
                </ul>
            </div>
        </AppModal>
    );
};

NotificationDialog.displayName = 'NotificationDialog';
export default NotificationDialog;
